import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import {
  StateFilterCBComponent
} from '../../utils/component/state-filter-combobox/state-cb.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TranslocoModule } from '@jsverse/transloco';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import { ModuleEntityService } from '../../module/services/module.entity.service';
import { LearningUnitEntityService } from '../services/learning-unit.entity.service';
import { LearningSectionEntityService } from '../../learning-section/services/learning-section.entity.service';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, switchMap, tap } from 'rxjs';
import { LearningUnitEntity } from '../learning-unit-entity';
import { ModuleEntity } from '../../module/module.entity';
import { LearningSectionEntity } from '../../learning-section/learning-section-entity';
import { map } from 'rxjs/operators';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import {
  FormDialogLearningUnitComponent
} from '../form-dialog-learning-unit/form-dialog-learning-unit.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';
import { HasApprovalState, StateBadgeComponent } from '../../core/components/state-badge/state-badge.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'pixl-learning-unit-main',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    DatePipe,
    MatTooltip,
    MatIconButton,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    StateFilterCBComponent,
    FreeFilterInputComponent,
    AsyncPipe,
    TranslocoModule,
    MultiComboEntitySearchComponent,
    TableLoadingSpinnerComponent,
    MatPaginator,
    StateBadgeComponent
  ],
  templateUrl: './learning-unit-main.component.html',
  styleUrl: './learning-unit-main.component.scss'
})
export class LearningUnitMainComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private _liveAnnouncer = inject(LiveAnnouncer);

  public learningUnitService = inject(LearningUnitEntityService);
  private moduleService = inject(ModuleEntityService);
  private learningSectionService = inject(LearningSectionEntityService);
  private consumerService = inject(ConsumerEntityService);
  private localStorageService = inject(LocalStorageService);
  private labelLearningSections: string[] = [];
  private labelModules: string[] = [];

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  public readonly learningUnitStateClass = LearningUnitEntity as unknown as new () => HasApprovalState;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  modules$!: Observable<ModuleEntity[]>;
  learningSections$!: Observable<LearningSectionEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;

  selectedModules: ModuleEntity[] = [];
  currentPageModules = 1;
  pageSizeModules = 20;
  searchQueryModules: string | null = null;

  selectedLearningSections: LearningSectionEntity[] = [];
  currentPageLearningSections = 1;
  pageSizeLearningSections = 20;
  searchQueryLearningSections: string | null = null;

  public refresh$ = new BehaviorSubject<void>(undefined);

  public displayedColumns: string[] = ['actions', 'id', 'title', 'subtitle', 'consumer', 'modules', 'learningSections', 'didacticHint', 'created_at', 'status'];

  totalItems = 1000;
  private sortString = 'title';

  private destroyRef = inject(DestroyRef);
  private readonly pageChange$ = toObservable(this.pageChange);

  ngOnInit() {
    this.loadRelationData();

    this.learningUnits$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'modules,learningSections,consumer',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrTitleLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumer][id]'] = this.consumerFilter.join(',');
        }

        if (this.moduleFilter?.length > 0) {
          query['filter[modules][id]'] = this.moduleFilter.join(',');
        }

        if (this.learningSectionFilter?.length > 0) {
          query['filter[learningSections][id]'] = this.learningSectionFilter.join(',');
        }

        if (this.statusFilter?.length > 0) {
          query['filter[status]'] = this.statusFilter.trim();
        }

        return from(this.learningUnitService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.learningUnitService.allByQuery(query))),
          map((learningUnits: LearningUnitEntity[]) => learningUnits.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.learningUnitService.stateClass) as EntityStateModel<LearningUnitEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const moduleQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };

    this.modules$ = from(
      this.moduleService.ensureLoaded(moduleQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.moduleService.allByQuery(moduleQuery)).pipe(
          map(paginated => {
            return [...this.selectedModules, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );

    const learningSectionQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };

    this.learningSections$ = from(
      this.learningSectionService.ensureLoaded(learningSectionQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningSectionService.allByQuery(learningSectionQuery)).pipe(
          map(paginated => {
            return [...this.selectedLearningSections, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogLearningUnitComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<LearningUnitEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new LearningUnitEntity(), result);
        await this.learningUnitService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.showMessage('Lerneinheit erfolgreich erstellt.');
      }
    });
  }

  onEdit(learningUnit: LearningUnitEntity) {
    this.currentSelectedRow.set(learningUnit.id);

    const dialogRef = this.dialog.open(FormDialogLearningUnitComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...learningUnit }
    });

    dialogRef.afterClosed().subscribe(async (updatedLearningUnit: LearningUnitEntity | undefined) => {
      if (!updatedLearningUnit) return;
      await firstValueFrom(this.learningUnitService.update(updatedLearningUnit));
      this.refresh$.next();
      this.showMessage('Lerneinheit erfolgreich editiert.');
    });
  }

  async onCopy(learningUnit: LearningUnitEntity) {
    this.currentSelectedRow.set(learningUnit.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(learningUnit.title),
            headerText: 'Lerneinheit kopieren',
            afterNameText: 'wirklich kopieren?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const copyEntityInstance = Object.assign(new LearningUnitEntity(), learningUnit);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.title = `${copyEntityInstance.title} (Kopie)`;

    // Ensure modules relationship is populated from moduleIds if missing
    if ((!copyEntityInstance.modules || copyEntityInstance.modules.length === 0) && copyEntityInstance.moduleIds?.length) {
      copyEntityInstance.modules = copyEntityInstance.moduleIds.map((id: string) => ({
        id,
        type: 'modules'
      }));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (copyEntityInstance as any).moduleIds;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (copyEntityInstance as any).learningSectionIds;

    await this.learningUnitService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Lerneinheit erfolgreich dupliziert.');
  }

  async onDelete(learningUnit: LearningUnitEntity) {
    if (!learningUnit?.id) {
      console.error('ID is undefined!', learningUnit);
      return;
    }

    this.currentSelectedRow.set(learningUnit.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(learningUnit.title),
            headerText: 'Lerneinheit löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.learningUnitService.delete(learningUnit.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Lerneinheit erfolgreich gelöscht.');
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  getModuleNames(modules: { id: string }[]): string[] {
    if (!modules?.length) return [];

    const state = this.store.selectSnapshot(this.moduleService.stateClass);

    return modules.map(module => {
      const entity = this.moduleService.stateClass.byId(state)(module.id);
      return entity?.title || module.id;
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  getLearningSectionNames(learningSections: { id: string }[]): string[] {
    if (!learningSections?.length) return [];

    const state = this.store.selectSnapshot(this.learningSectionService.stateClass);

    return learningSections.map(learningSection => {
      const entity = this.learningSectionService.stateClass.byId(state)(learningSection.id);
      return entity?.name || learningSection.id;
    });
  }

  receiveModuleData(selected: ModuleEntity[] | null) {
    this.selectedModules = selected ?? [];
    const selectedModules = (selected ?? []).map(module => module.id);
    this.labelModules = (selected ?? []).map(module => module.title);
    this.setModuleFilter(selectedModules);
  }

  async receiveModuleQuery(query: string) {
    this.searchQueryModules = query?.trim() || null;

    if (this.searchQueryModules && this.searchQueryModules.length > 3) {
      // search mode
      const searchParams = { 'filter[idOrTitleLike]': this.searchQueryModules };
      await this.moduleService.ensureLoaded(searchParams);

      this.modules$ = this.store.select(this.moduleService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      const pageQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };
      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreModules(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryModules) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.moduleService.ensureLoaded(pageQuery);

      const currentModules = await firstValueFrom(this.modules$ ?? of([]));

      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedModules,
            ...currentModules,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get moduleLabels(): string {
    return this.labelModules?.length ?
      this.labelModules.join(', ') :
      'Alle Module';
  }

  receiveLearningSectionData(selected: LearningSectionEntity[] | null) {
    this.selectedLearningSections = selected ?? [];
    const selectedSections = (selected ?? []).map(lu => lu.id);
    this.labelLearningSections = (selected ?? []).map(lu => lu.name);
    this.setLearningSectionFilter(selectedSections);
  }

  async receiveLearningSectionQuery(query: string) {
    this.searchQueryLearningSections = query?.trim() || null;

    if (this.searchQueryLearningSections && this.searchQueryLearningSections.length > 3) {
      // search mode
      const searchParams = { 'filter[idOrNameLike]': this.searchQueryLearningSections };
      await this.learningSectionService.ensureLoaded(searchParams);

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const pageQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };
      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreLearningSections(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningSections) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      await this.learningSectionService.ensureLoaded(pageQuery);

      const currentLearningSections = await firstValueFrom(this.learningSections$ ?? of([]));

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedLearningSections,
            ...currentLearningSections,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get learningSectionLabels(): string {
    return this.labelLearningSections?.length ?
      this.labelLearningSections.join(', ') :
      'Alle Lernabschnitte';
  }

  trackById(index: number, module: ModuleEntity): string {
    return module.id;
  }
}
